import {
  KeyRound,
  Pencil,
  Plus,
  Shield,
  ShieldOff,
  Trash2,
  UserRoundCheck,
  Users,
  UserX,
} from "lucide-react";
import { useState } from "react";
import { ActionCell, type ActionItem } from "@/components/shared/action-cell";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { useTableSort } from "@/components/shared/use-table-sort";
import { ViewToggle } from "@/components/shared/view-toggle";
import { Button } from "@/components/ui/button";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type User, useUnbanUser, useUsers } from "@/hooks/use-users";
import { useViewMode } from "@/hooks/use-view-mode";
import { useSession } from "@/lib/auth-client";
import { canManageUsers } from "@/lib/permissions";
import { APP_ROLES, ELEVATED_ROLES } from "@/schemas/users";
import { AppRoleBadge } from "./AppRoleBadge";
import { BanUserDialog } from "./BanUserDialog";
import { ChangeRoleDialog } from "./ChangeRoleDialog";
import { CreateUserDialog } from "./CreateUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { EditUserDialog } from "./EditUserDialog";
import { LinkedPerson } from "./LinkedPerson";
import { LinkPersonDialog } from "./LinkPersonDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { UserCards } from "./UserCards";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; user: User }
  | { type: "resetPassword"; user: User }
  | { type: "changeRole"; user: User }
  | { type: "linkPerson"; user: User }
  | { type: "ban"; user: User }
  | { type: "delete"; user: User };

const LIMIT = 50;

export function UsersTable() {
  const { data: session } = useSession();
  // Account mutations are Better Auth's, granted by role name — a view-only
  // admin can REACH this page (it is a view-tier route) but every action here
  // would be refused server-side, so the affordances must not be offered.
  const canManage = canManageUsers(session?.user?.role);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const { sort, toggleSort } = useTableSort("name");
  // Admins and managers by default: holding a tier above User is the exception,
  // and burying those few rows in every account in the company is what made the
  // page hard to read. The list is still ALL accounts — one chip away — because
  // banning a leaver and fixing a wrong person-link both live here and apply to
  // ordinary accounts too.
  //
  // An EMPTY selection means no filter (the group's "All" chip), matching every
  // other filter group in the app — so the default is the elevated tiers
  // selected, not a separate boolean.
  const [roleFilter, setRoleFilter] = useState<string[]>(ELEVATED_ROLES);
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<{
    cursor: string | null;
    direction: "next" | "prev";
  }>({ cursor: null, direction: "next" });
  const unbanUser = useUnbanUser();
  const [view, setView] = useViewMode("users.view");

  const { data, isLoading, isError, error, refetch } = useUsers(
    { search: debouncedSearch, sort: sort.key, dir: sort.dir, roles: roleFilter },
    pageRequest,
  );
  const users = data?.data ?? [];

  /**
   * How many accounts the tier filter is hiding, or null when the line should
   * not be shown: while loading, with no filter on, while a search is narrowing
   * the list (the difference would then count search misses too, not just
   * filtered-out tiers), and when nothing is hidden.
   */
  const hiddenCount =
    roleFilter.length > 0 && !isLoading && !debouncedSearch && data
      ? Math.max(0, data.totalAll - data.total) || null
      : null;

  /** True for the default view, where every hidden account is a plain User. */
  const showingElevatedOnly =
    roleFilter.length === ELEVATED_ROLES.length &&
    ELEVATED_ROLES.every((r) => roleFilter.includes(r));

  // With the default filter on, "nothing here" usually means nobody has been
  // given a tier yet — not that the search was wrong.
  const emptyDescription = showingElevatedOnly
    ? "No account has been made an admin or manager yet. Choose All accounts to see everyone."
    : "Try a different search, or choose All accounts.";

  function resetPage() {
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }

  function closeDialog() {
    setDialog({ type: "none" });
  }

  function buildActions(user: User): ActionItem[] {
    if (!canManage) return [];
    const items: ActionItem[] = [
      {
        icon: Pencil,
        label: "Edit Details",
        onClick: () => setDialog({ type: "edit", user }),
      },
      {
        icon: Shield,
        label: "Change Role",
        onClick: () => setDialog({ type: "changeRole", user }),
      },
      {
        icon: KeyRound,
        label: "Reset Password",
        onClick: () => setDialog({ type: "resetPassword", user }),
      },
      {
        icon: UserRoundCheck,
        label: "Linked Person",
        onClick: () => setDialog({ type: "linkPerson", user }),
      },
    ];
    if (user.banned) {
      items.push({
        icon: ShieldOff,
        label: "Unban User",
        onClick: () => unbanUser.mutate(user.id),
        disabled: unbanUser.isPending,
        separatorBefore: true,
      });
    } else {
      items.push({
        icon: UserX,
        label: "Ban User",
        onClick: () => setDialog({ type: "ban", user }),
        variant: "destructive",
        separatorBefore: true,
      });
    }
    items.push({
      icon: Trash2,
      label: "Delete User",
      onClick: () => setDialog({ type: "delete", user }),
      variant: "destructive",
    });
    return items;
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load users"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      <DataToolbar
        search={
          <SearchInput
            value={search}
            onValueChange={(value) => {
              setSearch(value);
              resetPage();
            }}
            placeholder="Search name or email…"
            className="flex-1 max-w-sm"
          />
        }
        actions={
          <>
            <ViewToggle value={view} onChange={setView} />
            {canManage && (
              <Button onClick={() => setDialog({ type: "create" })}>
                <Plus className="mr-2 h-4 w-4" />
                Create User
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-1.5">
        <FilterToggleGroup
          options={APP_ROLES}
          value={roleFilter}
          onChange={(next) => {
            setRoleFilter(next);
            resetPage();
          }}
          allLabel="All accounts"
        />
      </div>

      {view === "card" ? (
        <UserCards
          users={users}
          loading={isLoading}
          empty={<EmptyState icon={Users} title="No users found" description={emptyDescription} />}
          buildActions={buildActions}
          currentUserId={session?.user?.id}
        />
      ) : (
        <DataTable
          headers={[
            { label: "Name", sortKey: "name" },
            { label: "Email", sortKey: "email" },
            { label: "Role", sortKey: "role" },
            { label: "Person", className: "hidden lg:table-cell" },
            { label: "Created", sortKey: "createdAt", className: "hidden md:table-cell" },
            { label: "Actions", className: "text-right" },
          ]}
          loading={isLoading}
          sort={sort}
          onSort={(key) => {
            toggleSort(key);
            resetPage();
          }}
          empty={<EmptyState icon={Users} title="No users found" description={emptyDescription} />}
        >
          {users.map((user) => (
            <tr
              key={user.id}
              className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
            >
              <td className="px-4 py-2.5 font-medium">{user.name}</td>
              <td className="px-4 py-2.5 text-muted-foreground">{user.email}</td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-1.5">
                  <AppRoleBadge role={user.role} />
                  {user.banned && (
                    <span
                      className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
                      title={user.banReason ?? "Banned"}
                    >
                      Banned
                    </span>
                  )}
                </div>
              </td>
              <td className="hidden lg:table-cell px-4 py-2.5">
                <LinkedPerson user={user} />
              </td>
              <td className="hidden md:table-cell px-4 py-2.5 text-muted-foreground">
                {new Date(user.createdAt).toLocaleDateString()}
              </td>
              <td className="px-4 py-2.5 text-right">
                <ActionCell
                  items={buildActions(user)}
                  disabled={user.id === session?.user?.id}
                  triggerLabel={`Actions for ${user.name}`}
                />
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      {hiddenCount !== null && (
        // States the rule on screen. Without it a list of three reads as a
        // company of three rather than a filtered view, and nothing else says
        // that an account not shown here is a User. Deliberately does not open
        // with "Showing" — the pagination line below already does, and two of
        // them read as a contradiction.
        <p className="text-muted-foreground text-xs">
          {showingElevatedOnly
            ? `The other ${hiddenCount} ${hiddenCount === 1 ? "account is a User" : "accounts are Users"}.`
            : `${hiddenCount} ${hiddenCount === 1 ? "account is" : "accounts are"} hidden by this filter.`}{" "}
          <button
            type="button"
            className="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
            onClick={() => {
              setRoleFilter([]);
              resetPage();
            }}
          >
            Show all accounts
          </button>
        </p>
      )}

      <CursorPagination
        total={data?.total ?? 0}
        page={page}
        limit={LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        loading={isLoading}
        itemLabel="users"
        onNext={() => {
          if (!data?.nextCursor) return;
          setPage((p) => p + 1);
          setPageRequest({ cursor: data.nextCursor, direction: "next" });
        }}
        onPrev={() => {
          if (!data?.prevCursor) return;
          setPage((p) => Math.max(1, p - 1));
          setPageRequest({ cursor: data.prevCursor, direction: "prev" });
        }}
      />

      <CreateUserDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
      />

      {dialog.type === "edit" && (
        <EditUserDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "ban" && (
        <BanUserDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "linkPerson" && (
        <LinkPersonDialog open onOpenChange={closeDialog} user={dialog.user} />
      )}

      {dialog.type === "resetPassword" && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "changeRole" && (
        <ChangeRoleDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteUserDialog open onOpenChange={(open) => !open && closeDialog()} user={dialog.user} />
      )}
    </div>
  );
}
